import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartItem } from '@/lib/shopify';
import { trackZhFunnelIfMandarin } from '@/lib/zhFunnel';
import { trackShopping } from '@/lib/shopAnalytics';

export type { CartItem } from '@/lib/shopify';

/**
 * Local cart of Tallivo variants (identified by variantId). Prices are kept
 * for display only; checkout re-prices every line from Tallivo on the server.
 */
interface CartStore {
  items: CartItem[];
  isLoading: boolean;
  isSyncing: boolean;
  addItem: (item: Omit<CartItem, 'lineId'>) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<string | null>;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isLoading: false,
      isSyncing: false,

      addItem: async (item) => {
        trackZhFunnelIfMandarin("Add to Cart");
        const items = get().items;
        const existing = items.find((i) => i.variantId === item.variantId);
        set({
          items: existing
            ? items.map((i) => (i.variantId === item.variantId ? { ...i, quantity: Math.min(99, i.quantity + item.quantity) } : i))
            : [...items, { ...item, lineId: item.variantId }],
        });
        trackShopping("add_to_cart", { product_handle: item.product.node.handle, variant_id: item.variantId, quantity: item.quantity });
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) return get().removeItem(variantId);
        set({ items: get().items.map((i) => (i.variantId === variantId ? { ...i, quantity: Math.min(99, quantity) } : i)) });
      },

      removeItem: async (variantId) => {
        set({ items: get().items.filter((i) => i.variantId !== variantId) });
      },

      clearCart: () => set({ items: [] }),
      syncCart: async () => null,
    }),
    {
      name: 'tallivo-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
);
